import { useState } from 'react';
import { X, History } from 'lucide-react';
import { useScheduleVersions, useRestoreScheduleVersion } from '../../hooks/useScheduleVersions';
import { useAuthStore } from '../../stores/auth.store';
import DogLoader from '../ui/DogLoader';
import { VersionHistoryPanel } from '../version-control/VersionHistoryPanel';
import { VersionComparisonModal } from '../version-control/VersionComparisonModal';

// Simple toast replacement until sonner is installed
const toast = {
  success: (message: string) => alert(message),
  error: (message: string) => alert(message),
};

interface VersionHistorySidebarProps {
  isOpen: boolean;
  onClose: () => void;
  currentScheduleId: string;
}

export default function VersionHistorySidebar({
  isOpen,
  onClose,
  currentScheduleId,
}: VersionHistorySidebarProps) {
  const user = useAuthStore((state: any) => state.user);
  const { data: versions, isLoading } = useScheduleVersions(currentScheduleId);
  const restoreVersion = useRestoreScheduleVersion();
  const [isComparisonModalOpen, setIsComparisonModalOpen] = useState(false);
  const [comparison, setComparison] = useState<any>(null);

  const handleRestore = async (versionNumber: number) => {
    if (!confirm(`Are you sure you want to restore to version ${versionNumber}? This will replace the current schedule with the selected version.`)) {
      return;
    }

    try {
      await restoreVersion.mutateAsync({
        scheduleId: currentScheduleId,
        versionNumber,
        restoredBy: user?.userId,
      });
      toast.success(`Successfully restored to version ${versionNumber}`);
      onClose();
    } catch (error) {
      toast.error('Failed to restore version');
      console.error('Restore error:', error);
    }
  };

  const handleCompare = async (version1: number, version2: number) => {
    try {
      // Fetch comparison
      const response = await fetch(
        `/api/schedules/${currentScheduleId}/versions/compare/${version1}/${version2}`
      );
      const data = await response.json();
      setComparison(data);
      setIsComparisonModalOpen(true);
    } catch (error) {
      toast.error('Failed to compare versions');
      console.error('Compare error:', error);
    }
  };

  const currentVersion = versions?.[0];

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Sidebar */}
      <div className="fixed right-0 top-0 h-full w-[600px] bg-white shadow-2xl z-50 transform transition-transform overflow-y-auto">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <History className="w-5 h-5 text-gray-600" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Version History</h2>
                  <p className="text-sm text-gray-600 mt-0.5">
                    {versions?.length || 0} version{versions?.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Version List */}
          <div className="flex-1 p-6">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <DogLoader />
              </div>
            ) : (
              <VersionHistoryPanel
                versions={versions || []}
                isLoading={isLoading}
                onRestore={handleRestore}
                onCompare={handleCompare}
                currentVersionNumber={currentVersion?.versionNumber}
              />
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 sticky bottom-0">
            <p className="text-xs text-gray-600">
              Version history tracks all changes to this schedule. You can restore any previous version or compare differences between versions.
            </p>
          </div>
        </div>
      </div>

      {/* Comparison Modal */}
      <VersionComparisonModal
        isOpen={isComparisonModalOpen}
        onClose={() => {
          setIsComparisonModalOpen(false);
          setComparison(null);
        }}
        comparison={comparison}
        isLoading={false}
      />
    </>
  );
}
